import './styles/variables.css'
import './styles/reset.css'
import './styles/components.css'
import './styles/landing.css'
import './styles/lobby.css'
import './styles/meeting.css'

import { selfId } from 'trystero'
import type { Room } from 'trystero'

// Error handling & feature detection
import { hasWebRTC, hasSFrame, hasIndexedDB, showErrorBanner, isCameraDeniedError } from './errors'

// New modules
import { getOrCreateIdentity, exportPublicKeyBase64 } from './crypto/identity'
import { generateChallenge, solveChallenge } from './crypto/pow'
import type { Identity, PowSolution } from './types'
import { createMeetingRoom } from './discovery/room'
import {
  generateMeetingCode, formatCodeInput, isValidCode,
  generateEventLink, parseMeetingUrl,
} from './discovery/invite'
import {
  getMediaStream, stopAllTracks, enumerateMediaDevices,
  switchCamera, switchMicrophone, muteAudio, muteVideo,
} from './media/camera'
import {
  startSpeakerDetection, stopSpeakerDetection,
  onActiveSpeakerChange, addAudioSource, removeAudioSource,
} from './media/speaker'
import { startMonitoring, stopAllMonitoring } from './media/bandwidth'
import { setupConnections, teardownConnections } from './connection'
import type { LocalIdentity } from './core/signaling'
import { loadDisplayName, saveDisplayName } from './state/persistence'
import { meetingStore, createMeeting, joinMeeting as joinMeetingState, startCall, callConnected, endCall, resetMeeting, setDisplayName, setLocalPeerId, cancelLobby } from './state/meeting'
import {
  lobbyStore, lobbyCodeParsed, lobbyMediaReady, lobbyJoin,
  lobbyConnected, lobbyReset, lobbySetError,
} from './state/lobby'
import {
  participantStore,
  addParticipant as addParticipantToStore,
  removeParticipant as removeParticipantFromStore,
  updateParticipant as updateParticipantInStore,
  clearParticipants,
} from './state/participants'

// UI
import {
  els, initIcons, showLanding, showLobby, showMeeting,
  addVideoTile, removeVideoTile, updateTileState, updateToggle,
  setStatus, showError, clearMeeting,
  addParticipant as addParticipantUI, removeParticipant as removeParticipantUI,
  updateParticipantMic,
  togglePanel, closePanel,
  populateDeviceSelect, setLobbyPreview,
} from './ui'

// ============================================================
// State
// ============================================================

let room: Room | null = null
let localStream: MediaStream | null = null
let displayName = ''
let micOn = true
let camOn = true
let unsubSpeaker: (() => void) | null = null
let localIdentity: Identity | null = null
let localPeerId: string = selfId // fallback to trystero selfId until identity loads
// Stored for future peer verification over DataChannel
export let localPowProof: { challenge: Uint8Array; solution: PowSolution } | null = null

// ============================================================
// Identity initialization
// ============================================================

const identityReady: Promise<Identity | null> = getOrCreateIdentity()
  .then((identity) => {
    localIdentity = identity
    localPeerId = identity.participantId
    return identity
  })
  .catch((err) => {
    console.warn('[main] identity init failed, using trystero selfId:', err)
    return null
  })

// ============================================================
// Store subscriptions — drive UI reactively
// ============================================================

meetingStore.subscribe((state) => {
  if (state.error) {
    setStatus(`Error: ${state.error}`)
  }
})

participantStore.subscribe((state) => {
  const count = Object.keys(state.participants).length
  const countEl = document.getElementById('participant-count')
  if (countEl) countEl.textContent = String(count)
})

lobbyStore.subscribe((state) => {
  if (state.error) {
    showError(state.error)
  }
})

// ============================================================
// Media acquisition
// ============================================================

async function acquireMedia(): Promise<void> {
  try {
    localStream = await getMediaStream()
  } catch (err) {
    if (isCameraDeniedError(err)) {
      lobbySetError('Camera/microphone permission denied')
      showErrorBanner(
        'Camera/mic access denied. Check your browser permissions and try again.',
        false,
      )
    } else {
      const msg = (err as Error).message
      lobbySetError(msg)
      showErrorBanner(`Camera/mic not available: ${msg}`)
    }
  }
}

// ============================================================
// Landing screen
// ============================================================

async function onCreateMeeting(): Promise<void> {
  const code = generateMeetingCode()
  createMeeting(code)
  lobbyCodeParsed(code)
  await acquireMedia()
  lobbyMediaReady(
    !!localStream?.getVideoTracks().length,
    !!localStream?.getAudioTracks().length,
  )
  enterLobby()
}

async function onJoinWithCode(): Promise<void> {
  const code = els.codeInput().value.trim()
  if (!isValidCode(code)) {
    els.codeInput().focus()
    return
  }
  joinMeetingState(code)
  lobbyCodeParsed(code)
  await acquireMedia()
  lobbyMediaReady(
    !!localStream?.getVideoTracks().length,
    !!localStream?.getAudioTracks().length,
  )
  enterLobby()
}

// Auto-format code input
els.codeInput().addEventListener('input', () => {
  const input = els.codeInput()
  const pos = input.selectionStart ?? 0
  const before = input.value
  input.value = formatCodeInput(before)
  const diff = input.value.length - before.length
  input.setSelectionRange(pos + diff, pos + diff)
})

// ============================================================
// Lobby
// ============================================================

async function enterLobby(): Promise<void> {
  showLobby()
  els.lobbyCode().textContent = lobbyStore.getState().meetingCode ?? ''

  // Restore saved name from IndexedDB
  const savedName = await loadDisplayName()
  if (savedName) {
    els.nameInput().value = savedName
    displayName = savedName
    setDisplayName(savedName)
  }

  // Show preview from already-acquired stream
  setLobbyPreview(localStream)
  if (localStream) {
    populateDevices()
  }
}

async function populateDevices(): Promise<void> {
  try {
    const { cameras, microphones } = await enumerateMediaDevices()
    const currentVideoId = localStream?.getVideoTracks()[0]?.getSettings().deviceId
    const currentAudioId = localStream?.getAudioTracks()[0]?.getSettings().deviceId
    populateDeviceSelect(els.micSelect(), microphones, currentAudioId)
    populateDeviceSelect(els.camSelect(), cameras, currentVideoId)
  } catch {
    // Device enumeration not supported or permission denied
  }
}

function leaveLobby(): void {
  if (localStream) {
    stopAllTracks(localStream)
    localStream = null
  }
  setLobbyPreview(null)
  lobbyReset()
  cancelLobby()
  showLanding()
}

async function onCopyLink(): Promise<void> {
  const link = generateEventLink(lobbyStore.getState().meetingCode ?? '')
  try {
    await navigator.clipboard.writeText(link)
    const btn = els.copyLinkBtn()
    const orig = btn.innerHTML
    btn.textContent = 'Copied!'
    setTimeout(() => { btn.innerHTML = orig }, 1500)
  } catch {
    // Clipboard API not available
  }
}

async function onDeviceChange(kind: 'videoinput' | 'audioinput', deviceId: string): Promise<void> {
  if (!localStream) return
  try {
    if (kind === 'videoinput') {
      await switchCamera(deviceId, localStream)
    } else {
      await switchMicrophone(deviceId, localStream)
    }
    setLobbyPreview(localStream)
  } catch (err) {
    showError(`Failed to switch device: ${(err as Error).message}`)
  }
}

// ============================================================
// Meeting — join and peer handling
// ============================================================

async function joinMeeting(): Promise<void> {
  lobbyJoin()

  // Ensure identity is resolved before joining
  await identityReady
  setLocalPeerId(localPeerId)

  displayName = els.nameInput().value.trim() || `User-${localPeerId.slice(0, 4)}`
  setDisplayName(displayName)

  // Persist display name to IndexedDB
  saveDisplayName(displayName).catch(() => {})

  // Compute proof of work (non-blocking via Web Worker)
  const joinBtn = els.lobbyJoinBtn()
  const origBtnHTML = joinBtn.innerHTML
  joinBtn.innerHTML = '<span class="spinner"></span>'
  joinBtn.disabled = true
  try {
    const challenge = generateChallenge()
    const solution = await solveChallenge(challenge)
    localPowProof = { challenge, solution }
  } catch (err) {
    console.warn('[main] PoW failed, continuing without proof:', err)
    localPowProof = null
  }
  joinBtn.innerHTML = origBtnHTML
  joinBtn.disabled = false

  // If we don't have a stream yet, try once more
  if (!localStream) {
    try {
      localStream = await getMediaStream()
    } catch (err) {
      if (isCameraDeniedError(err)) {
        showErrorBanner('Camera/mic denied — joining without video.', true)
      } else {
        showErrorBanner(`No camera/mic — joining without video. (${(err as Error).message})`)
      }
    }
  }

  // Export public key for participant store (null if identity failed)
  const localPublicKey = localIdentity
    ? await exportPublicKeyBase64(localIdentity.signingKeyPair.publicKey)
    : null

  startCall()
  showMeeting()
  clearMeeting()

  // Add local tile using identity-derived participant ID
  if (localStream) {
    addVideoTile(localStream, localPeerId, displayName, true)
  } else {
    addVideoTile(null, localPeerId, displayName, true)
    updateTileState(localPeerId, { videoMuted: true })
  }
  addParticipantUI(localPeerId, `${displayName} (You)`)
  addParticipantToStore(localPeerId, {
    displayName,
    audioMuted: !micOn,
    videoMuted: !camOn,
    connectionState: 'connected',
    isSpeaking: false,
    audioLevel: 0,
    joinedAt: Date.now(),
    publicKey: localPublicKey,
    capabilities: null,
  })

  const currentCode = lobbyStore.getState().meetingCode ?? ''
  setStatus(`joining "${currentCode}"...`)

  // Use new room creation with HMAC-derived room ID
  try {
    const meetingRoom = await createMeetingRoom(currentCode, (details) => {
      setStatus(`Relay error — retrying...`)
      showErrorBanner(`Signaling relay error: ${details.error}`)
    })
    room = meetingRoom.room

    callConnected()
    lobbyConnected()

    // Wire core/ modules (MeshManager + ProtocolManager) onto the Trystero room
    const bridgeIdentity: LocalIdentity = {
      peerId: localPeerId,
      publicKey: localPublicKey ?? '',
      displayName,
      meetingId: currentCode,
      meetingProof: '', // TODO: derive from PoW proof once verifier is wired
      capabilities: { sframe: false, svc: [], maxVideoTracks: 1, canRelay: false },
    }
    setupConnections(room, bridgeIdentity)
  } catch (err) {
    const msg = (err as Error).message
    showErrorBanner(`Failed to connect to meeting: ${msg}`, false)
    endCall(msg)
    return
  }

  // Start dominant speaker detection
  startSpeakerDetection()
  unsubSpeaker = onActiveSpeakerChange((speakers) => {
    // Update speaking state for all participants
    const allParticipants = participantStore.getState().participants
    for (const peerId of Object.keys(allParticipants)) {
      const isSpeaking = speakers.includes(peerId)
      updateTileState(peerId, { speaking: isSpeaking })
      updateParticipantInStore(peerId, { isSpeaking })
    }
  })

  room.onPeerJoin((peerId) => {
    setStatus(`peer joined: ${peerId.slice(0, 8)} — exchanging media...`)
    if (localStream) {
      room!.addStream(localStream, peerId)
    }

    // Start bandwidth monitoring for this peer's connection
    const peers = room!.getPeers()
    const pc = peers[peerId]
    if (pc) {
      startMonitoring(peerId, pc)
    }
  })

  room.onPeerStream((stream, peerId) => {
    const peerName = peerId.slice(0, 8)
    addVideoTile(stream, peerId, peerName)
    addParticipantUI(peerId, peerName)
    addParticipantToStore(peerId, {
      displayName: peerName,
      audioMuted: false,
      videoMuted: false,
      connectionState: 'connected',
      isSpeaking: false,
      audioLevel: 0,
      joinedAt: Date.now(),
      publicKey: null,
      capabilities: null,
    })
    setStatus(`connected to ${peerName}`)

    // Register audio track for speaker detection
    const audioTrack = stream.getAudioTracks()[0]
    if (audioTrack) {
      const peers = room!.getPeers()
      const pc = peers[peerId]
      if (pc) {
        const receiver = pc.getReceivers().find(
          (r) => r.track === audioTrack,
        )
        if (receiver) {
          addAudioSource(peerId, receiver)
        }
      }
    }
  })

  room.onPeerLeave((peerId) => {
    removeVideoTile(peerId)
    removeParticipantUI(peerId)
    removeParticipantFromStore(peerId)
    removeAudioSource(peerId)
    setStatus(`peer left: ${peerId.slice(0, 8)}`)
  })
}

// ============================================================
// Leave call
// ============================================================

function leaveCall(): void {
  // Tear down core/ bridge before leaving the Trystero room
  teardownConnections()

  if (room) {
    room.leave()
    room = null
  }
  if (localStream) {
    stopAllTracks(localStream)
    localStream = null
  }

  // Clean up speaker detection
  if (unsubSpeaker) {
    unsubSpeaker()
    unsubSpeaker = null
  }
  stopSpeakerDetection()
  stopAllMonitoring()

  // Reset state
  micOn = true
  camOn = true
  updateToggle(els.micBtn(), 'mic', micOn)
  updateToggle(els.camBtn(), 'cam', camOn)
  clearMeeting()
  clearParticipants()
  endCall()
  resetMeeting()
  lobbyReset()
  showLanding()
}

// ============================================================
// Media toggles
// ============================================================

function toggleMic(): void {
  if (!localStream) return
  micOn = !micOn
  muteAudio(localStream, !micOn)
  updateToggle(els.micBtn(), 'mic', micOn)
  updateTileState(localPeerId, { audioMuted: !micOn })
  updateParticipantMic(localPeerId, !micOn)
  updateParticipantInStore(localPeerId, { audioMuted: !micOn })
}

function toggleCam(): void {
  if (!localStream) return
  camOn = !camOn
  muteVideo(localStream, !camOn)
  updateToggle(els.camBtn(), 'cam', camOn)
  updateTileState(localPeerId, { videoMuted: !camOn })
  updateParticipantInStore(localPeerId, { videoMuted: !camOn })
}

// ============================================================
// Global error handler
// ============================================================

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  const message = reason instanceof Error ? reason.message : String(reason)
  console.error('[mrmr] Unhandled rejection:', reason)
  showErrorBanner(`Something went wrong: ${message}`)
})

// ============================================================
// Feature detection — check capabilities on startup
// ============================================================

if (!hasWebRTC()) {
  showErrorBanner(
    'Your browser does not support WebRTC. Please use a modern browser like Chrome, Firefox, or Safari.',
    false,
  )
  // Disable meeting controls since WebRTC is unavailable
  els.createMeetingBtn().disabled = true
  els.joinCodeBtn().disabled = true
}

if (!hasSFrame()) {
  console.warn('[mrmr] RTCRtpScriptTransform not supported — end-to-end encryption unavailable')
}

if (!hasIndexedDB()) {
  console.warn('[mrmr] IndexedDB not available — preferences will not persist across sessions')
}

// ============================================================
// Wire up events
// ============================================================

initIcons()

// Landing
els.createMeetingBtn().addEventListener('click', onCreateMeeting)
els.joinCodeBtn().addEventListener('click', onJoinWithCode)
els.codeInput().addEventListener('keydown', (e) => {
  if (e.key === 'Enter') onJoinWithCode()
})

// Check URL hash for meeting code using new parseMeetingUrl
const parsed = parseMeetingUrl(window.location.hash)
if (parsed) {
  joinMeetingState(parsed.code)
  lobbyCodeParsed(parsed.code)
  lobbyMediaReady(false, false)
  enterLobby()
}

// Lobby
els.lobbyBackBtn().addEventListener('click', leaveLobby)
els.copyLinkBtn().addEventListener('click', onCopyLink)
els.lobbyJoinBtn().addEventListener('click', joinMeeting)
els.micSelect().addEventListener('change', (e) => {
  onDeviceChange('audioinput', (e.target as HTMLSelectElement).value)
})
els.camSelect().addEventListener('change', (e) => {
  onDeviceChange('videoinput', (e.target as HTMLSelectElement).value)
})

// Meeting
els.micBtn().addEventListener('click', toggleMic)
els.camBtn().addEventListener('click', toggleCam)
els.leaveBtn().addEventListener('click', leaveCall)
els.participantsToggleBtn().addEventListener('click', togglePanel)
els.panelCloseBtn().addEventListener('click', closePanel)

window.addEventListener('beforeunload', leaveCall)
